import { set } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { degToRad, PI_OVER_2 } from "@skydio/math";
import { RecordingMode_Enum } from "@skydio/pbtypes/pbtypes/gen/user_camera/recording_mode";
import {
  GotoWaypointActionArgs,
  GotoWaypointActionArgs_SituationalAwarenessArgs,
  GotoWaypointActionArgs_SituationalAwarenessArgs_SituationalAwarenessActions,
} from "@skydio/pbtypes/pbtypes/vehicle/skills/actions/goto_waypoint/goto_waypoint";
import {
  GotoAndOrbitPoiActionArgs,
  InterceptOrbitPoiMode_Enum,
  OrbitPoiActionArgs,
} from "@skydio/pbtypes/pbtypes/vehicle/skills/actions/orbit_poi/orbit_poi";
import { SetObstacleAvoidanceActionArgs } from "@skydio/pbtypes/pbtypes/vehicle/skills/actions/set_obstacle_avoidance/set_obstacle_avoidance";
import { StopVideoActionArgs } from "@skydio/pbtypes/pbtypes/vehicle/skills/actions/stop_video/stop_video";
import { TakePhotoActionArgs } from "@skydio/pbtypes/pbtypes/vehicle/skills/actions/take_photo/take_photo";
import { TimedWaitActionArgs } from "@skydio/pbtypes/pbtypes/vehicle/skills/actions/timed_wait/timed_wait";
import { TogglePhotoIntervalModeActionArgs } from "@skydio/pbtypes/pbtypes/vehicle/skills/actions/toggle_photo_interval_mode/toggle_photo_interval_mode";
import {
  Action,
  ActionArgs,
  SequenceActionArgs,
} from "@skydio/pbtypes/pbtypes/vehicle/skills/mission";
import { PhotoFormatSettings_PhotoFormat } from "@skydio/pbtypes/pbtypes/vehicle/skills/mission_camera_settings";
import { ObstacleAvoidanceEnum_Enum } from "@skydio/pbtypes/pbtypes/vehicle/skills/obstacle_avoidance";
import {
  FrameType_Enum,
  FreeLookMode_Enum,
  GimbalPitchMode_Enum,
  HeadingMode_Enum,
  HeightMode_Enum,
  MotionArgs,
  Waypoint as WaypointPb,
} from "@skydio/pbtypes/pbtypes/vehicle/skills/waypoints";

import { getWaypointSpeedConstraints, isX10 } from "../constraints";
import { GPS_INIT_NAME } from "./types";

import type {
  FrameType,
  PositionXY,
  PositionZ,
  RampArgs,
} from "@skydio/pbtypes/pbtypes/vehicle/skills/waypoints";
import type {
  ActionType,
  GPSInitWaypoint,
  SurfaceScanSkill,
  VehicleConfiguration,
  Waypoint,
} from "./types";

const DEFAULT_ZOOM_LEVEL = 1;
const DEFAULT_X10_RECORDING_MODE = RecordingMode_Enum.VIDEO_1080P_DEEP_30FPS;
const DEFAULT_X2S2_RECORDING_MODE = RecordingMode_Enum.VIDEO_1080P_30FPS;
const DEFAULT_X10_PHOTO_MODE = RecordingMode_Enum.PHOTO_HIGH_RES;
const DEFAULT_X2S2_PHOTO_MODE = RecordingMode_Enum.PHOTO_DEFAULT;
const DEFAULT_X10_TIME_INTERVAL = 2;
const DEFAULT_X2S2_TIME_INTERVAL = 1;

/**
 * Get waypoint speed settings based on vehicle configuration
 * @param twilightMode - Whether in twilight mode (defaults to false)
 * @param vehicleConfiguration - Vehicle configuration
 * @returns Speed constraints object
 */
function getWaypointSpeed(
  twilightMode = false,
  vehicleConfiguration?: VehicleConfiguration
): { MIN: number; MAX: number; DEFAULT: number; DEFAULT_INDOOR: number } {
  const flightMode = twilightMode ? "twilight" : "nominal";
  return getWaypointSpeedConstraints(flightMode, vehicleConfiguration);
}

export function isGPSInitHover(action?: Action): action is GPSInitWaypoint {
  return action != null && isWaypoint(action) && action.args.sequence.name === GPS_INIT_NAME;
}

const ACTION_2_KEYS: Array<keyof ActionArgs> = [
  "startVideo",
  "stopVideo",
  "togglePhotoIntervalMode",
];
const ACTION_4_KEYS: Array<keyof ActionArgs> = [
  "timedWait",
  "takePanorama",
  "takePhoto",
  "setObstacleAvoidance",
  "togglePhotoIntervalMode",
];
const ACTION_5_KEYS: Array<keyof ActionArgs> = [
  "takePanorama",
  "takePhoto",
  "setObstacleAvoidance",
  "togglePhotoIntervalMode",
];

const ACTION_6_KEYS: Array<keyof ActionArgs> = [
  "takePanorama",
  "takePhoto",
  "setObstacleAvoidance",
];

export function isWaypoint(action: Action): action is Waypoint {
  if (!isActionType(action, "sequence")) {
    return false;
  }
  const subActions = action.args.sequence.actions;
  if (subActions.length < 4 || subActions.length > 7) {
    return false;
  }
  // Last action must be an obstacle avoidance reset
  if (!isActionType(subActions[subActions.length - 1], "setObstacleAvoidance")) {
    return false;
  }
  const firstActionIsOA = isActionType(subActions[0], "setObstacleAvoidance");
  const validSecondAction = ACTION_2_KEYS.find(key => isActionType(subActions[1], key)) != null;
  const thirdActionIsGoto = isActionType(subActions[2], "gotoWaypoint");
  const validFourthAction = ACTION_4_KEYS.find(key => isActionType(subActions[3], key)) != null;
  const validFifthAction =
    !subActions[4] || ACTION_5_KEYS.find(key => isActionType(subActions[4], key)) != null;
  const validSixthAction =
    !subActions[5] || ACTION_6_KEYS.find(key => isActionType(subActions[5], key)) != null;
  const validSeventhAction = !subActions[6] || isActionType(subActions[6], "setObstacleAvoidance");
  return (
    firstActionIsOA &&
    validSecondAction &&
    thirdActionIsGoto &&
    validFourthAction &&
    validFifthAction &&
    validSixthAction &&
    validSeventhAction
  );
}

export function isSurfaceScanSkill(action: Action): action is SurfaceScanSkill {
  if (!isActionType(action, "sequence")) {
    return false;
  }
  const surfaceScanAction = action.args.sequence.actions.at(-1);
  if (!surfaceScanAction) {
    return false;
  }
  return isActionType(surfaceScanAction, "surfaceScanSkill");
}

export function isActionType<T extends keyof ActionArgs>(
  action: Action | undefined,
  type: T
): action is ActionType<T> {
  if (!action) {
    return false;
  }
  return action.actionKey.toLowerCase() === type.toLowerCase() && action.args?.[type] != null;
}

export const ROOT_SEQUENCE_NAME = "root_sequence";

export function isRootSequence(action: Action): action is ActionType<"sequence"> {
  return (
    isActionType(action, "sequence") &&
    action.args.sequence.name === ROOT_SEQUENCE_NAME &&
    !!action.args.sequence.actions
  );
}

export function initActionType<T extends keyof ActionArgs>(
  key: T,
  val: ActionArgs[T]
): ActionType<T> {
  const actionKey = key.charAt(0).toUpperCase() + key.slice(1);
  const actionUuid = uuidv4();

  // @ts-expect-error - Complex protobuf oneof type inference limitation with fromPartial
  const args = ActionArgs.fromPartial({
    [key]: val,
  });

  const action = Action.fromPartial({
    actionKey,
    actionUuid,
    args,
  }) as ActionType<T>;

  return action;
}

function getMotionArgs(xyFrame: FrameType, informedPathPlanning?: boolean) {
  const lookAtArgs = {
    headingMode: HeadingMode_Enum.GRADUAL,
    gimbalPitchMode: GimbalPitchMode_Enum.GRADUAL,
  };
  const SPEED_MAP = {
    [FrameType_Enum.GPS]: getWaypointSpeed().DEFAULT,
    [FrameType_Enum.NAV_TAKEOFF]: getWaypointSpeed().DEFAULT_INDOOR,
  };
  const speed = SPEED_MAP[xyFrame as keyof typeof SPEED_MAP] ?? getWaypointSpeed().DEFAULT_INDOOR;

  if (informedPathPlanning && xyFrame === FrameType_Enum.NAV_TAKEOFF) {
    return {
      graphArgs: {
        speed: speed,
        allowedEdges: {
          vehiclePaths: true,
          loadedPaths: true,
        },
        useSpline: true,
      },
      lookAtArgs: lookAtArgs,
    };
  } else {
    return {
      traversalArgs: {
        heightMode: HeightMode_Enum.GRADUAL,
        speed: speed,
      },
      lookAtArgs: lookAtArgs,
    };
  }
}

export function createDefaultRecordVideoCameraSettings(
  vehicleConfiguration?: VehicleConfiguration
) {
  return {
    recordingMode: isX10(vehicleConfiguration)
      ? DEFAULT_X10_RECORDING_MODE
      : DEFAULT_X2S2_RECORDING_MODE,
    zoomLevel: DEFAULT_ZOOM_LEVEL,
  };
}

export function createDefaultPhotoSettings() {
  return {
    photoFormat: {
      format: "JPEG",
    },
    photoType: {
      eoPhoto: true,
      irPhoto: false,
    },
    photoFormatSettings: {
      eoPhotoFormats: [PhotoFormatSettings_PhotoFormat.JPEG],
      irPhotoFormats: [],
    },
  };
}

export function createDefaultTogglePhotoIntervalModeActionArgs(
  vehicleConfiguration?: VehicleConfiguration
) {
  return TogglePhotoIntervalModeActionArgs.fromPartial({
    timeInterval: isX10(vehicleConfiguration)
      ? DEFAULT_X10_TIME_INTERVAL
      : DEFAULT_X2S2_TIME_INTERVAL,
    photoQuality: 100,
    enable: true,
    useTimeInterval: true,
    photoSettings: createDefaultPhotoSettings(),
  });
}

export function createDefaultTogglePhotoIntervalModeAction(
  vehicleConfiguration?: VehicleConfiguration
) {
  return initActionType(
    "togglePhotoIntervalMode",
    createDefaultTogglePhotoIntervalModeActionArgs(vehicleConfiguration)
  );
}

export function createDefaultTakePhotoAction(vehicleConfiguration?: VehicleConfiguration) {
  return initActionType(
    "takePhoto",
    TakePhotoActionArgs.fromPartial({
      photoSettings: createDefaultPhotoSettings(),
      cameraSettings: {
        recordingMode: isX10(vehicleConfiguration)
          ? DEFAULT_X10_PHOTO_MODE
          : DEFAULT_X2S2_PHOTO_MODE,
        zoomLevel: DEFAULT_ZOOM_LEVEL,
      },
      waitForFocusToConverge: true,
    })
  );
}

function createDefaultStopVideoAction() {
  return initActionType("stopVideo", StopVideoActionArgs.create());
}

function createDefaultSetObstacleAvoidanceAction() {
  return initActionType(
    "setObstacleAvoidance",
    SetObstacleAvoidanceActionArgs.fromPartial({
      oaSetting: ObstacleAvoidanceEnum_Enum.DEFAULT,
    })
  );
}

const supportedFrames = new Set([FrameType_Enum.SITE, FrameType_Enum.NAV_TAKEOFF]);

function chooseZFrame(zFrame: FrameType_Enum | undefined, xyFrame: FrameType_Enum): FrameType_Enum {
  if (zFrame != null) return zFrame;
  if (supportedFrames.has(xyFrame)) return xyFrame;
  return FrameType_Enum.NAV_TAKEOFF;
}

function createDefaultGotoWaypointAction(
  xyFrame: FrameType_Enum,
  zFrame?: FrameType_Enum,
  informedPathPlanning?: boolean
) {
  return set(initActionType("gotoWaypoint", GotoWaypointActionArgs.create()), "args.gotoWaypoint", {
    waypoint: {
      xy: {
        frame: xyFrame,
      },
      z: {
        frame: chooseZFrame(zFrame, xyFrame),
      },
      heading: {
        // rotations are counter clockwise from due East. GPS should init to pointing north (0 degrees on the UI)
        value: xyFrame === FrameType_Enum.GPS ? PI_OVER_2 : 0,
        frame: xyFrame,
      },
      gimbalPitch: {
        value: 0,
      },
    },
    motionArgs: getMotionArgs(xyFrame, informedPathPlanning),
    // We default to this because a default waypoint has a StopVideo action
    situationalAwarenessArgs: createDefaultGotoWaypointSituationalAwarenessArgs(),
  });
}

export function createDefaultGotoWaypointSituationalAwarenessArgs() {
  return GotoWaypointActionArgs_SituationalAwarenessArgs.fromPartial({
    minWaypointTaskTime: 2.0,
    situationalAwarenessActions:
      GotoWaypointActionArgs_SituationalAwarenessArgs_SituationalAwarenessActions.fromPartial({
        zoomOut: true,
      }),
  });
}

export function createDefaultWaypoint(initParams: {
  xyFrame: FrameType_Enum;
  zFrame?: FrameType_Enum;
  timedWaitInit?: ActionType<"timedWait">;
  informedPathPlanning?: boolean;
  vehicleConfiguration?: VehicleConfiguration;
}): Waypoint {
  const { xyFrame, zFrame, informedPathPlanning, timedWaitInit, vehicleConfiguration } = initParams;

  const actions = [
    createDefaultSetObstacleAvoidanceAction(),
    createDefaultStopVideoAction(),
    createDefaultGotoWaypointAction(xyFrame, zFrame, informedPathPlanning),
    ...(timedWaitInit ? [timedWaitInit] : []),
    createDefaultTakePhotoAction(vehicleConfiguration),
    createDefaultSetObstacleAvoidanceAction(),
  ];

  const sequenceArgs = SequenceActionArgs.fromPartial({ actions });
  const baseSequence = initActionType("sequence", sequenceArgs);

  if (!isWaypoint(baseSequence)) {
    throw new Error(
      "createNewWaypoint method is bugged, doesn't match waypoint spec. Check TypeGuards.ts"
    );
  }

  return baseSequence;
}

export const FLY_BY_LATERAL_POSITION_THRESHOLD = 10;
export const FLY_BY_VERTICAL_POSITION_THRESHOLD = 5;

export function getFlyToPoiMotionArgs(
  /**
   * If true, the vehicle will ignore the camera orientation at the waypoint
   */
  ignoreCameraOrientation: boolean,
  /**
   * If true, the vehicle will mark the waypoint as done without stopping precisely at it. This is useful for waypoints you want to fly past instead of stop at.
   */
  flyByWaypoint: boolean,
  /**
   * If true, the vehicle will use geofence, building and terrain data to inform its path planning.
   */
  globalPathfinderEnabled?: boolean,
  /**
   * If true, the vehicle will plan around geofences.
   */
  planAroundGeofences?: boolean,
  /**
   * If provided, the vehicle will use the given ramp to get to the waypoint
   */
  rampArgs?: RampArgs,
  /**
   * If provided, the vehicle will look at this point at the given height on it's way to the waypoint.
   */
  lookatPoint?: PositionXY,
  lookatHeight?: PositionZ,
  speed?: number
): MotionArgs {
  const lookAtArgs =
    lookatPoint != null && lookatHeight != null
      ? {
          headingMode: HeadingMode_Enum.LOOKAT_POINT,
          gimbalPitchMode: GimbalPitchMode_Enum.LOOKAT_POINT,
          ignoreTargetGimbalPitch: ignoreCameraOrientation,
          ignoreTargetHeading: ignoreCameraOrientation,
          lookatPoint,
          lookatHeight,
        }
      : {
          headingMode:
            planAroundGeofences || globalPathfinderEnabled
              ? HeadingMode_Enum.LOOKAHEAD
              : HeadingMode_Enum.FORWARD,
          gimbalPitchMode: GimbalPitchMode_Enum.CUSTOM,
          customGimbalPitch: {
            value: 0.0,
          },
          ignoreTargetGimbalPitch: ignoreCameraOrientation,
          ignoreTargetHeading: ignoreCameraOrientation,
        };

  // NOTE(divyanshu): This is hacked to give us the max possible speed without taking into account
  // whether we are in twilight mode since fly to point is meant for us to go as fast as possible
  // and this speed setting is not displayed to the user. The vehicle will clamp the speed if it is
  // unsafe.
  const traversalSpeed = speed ?? getWaypointSpeed(false).MAX;
  const terminationArgs = {
    xyPositionThreshold: flyByWaypoint ? FLY_BY_LATERAL_POSITION_THRESHOLD : 0.1,
    zPositionThreshold: flyByWaypoint ? FLY_BY_VERTICAL_POSITION_THRESHOLD : 0.1,
  };

  return MotionArgs.fromPartial({
    traversalArgs: {
      heightMode: rampArgs ? HeightMode_Enum.RAMP : HeightMode_Enum.CONSTANT_END,
      speed: traversalSpeed,
      usePathfinder: globalPathfinderEnabled || planAroundGeofences,
      useGlobalPathfinder: globalPathfinderEnabled ?? false,
      rampArgs: rampArgs,
    },
    lookAtArgs,
    terminationArgs,
  });
}

/**
 * Creates a new POI waypoint. This can be either a POI or a fly-by waypoint. The difference is that
 * a fly-by waypoint is a waypoint where the vehicle won't stop at. A POI is a termination
 * waypoint. The vehicle will stop at the POI. In both cases, the camera orientation is ignored.
 */
export function createNewPoiWaypoint(initParams: {
  xyFrame: FrameType_Enum;
  zFrame?: FrameType_Enum;
  flyByWaypoint?: boolean;
  globalPathfinderEnabled?: boolean;
  planAroundGeofences?: boolean;
  rampArgs?: RampArgs;
  speed?: number;
}): ActionType<"gotoWaypoint"> {
  const {
    xyFrame,
    zFrame,
    flyByWaypoint = false,
    globalPathfinderEnabled = false,
    planAroundGeofences = false,
    rampArgs = undefined,
    speed = undefined,
  } = initParams;
  const supportedFrames = new Set([FrameType_Enum.SITE, FrameType_Enum.NAV_TAKEOFF]);
  // Setting this to true for all waypoints, as the user can't control this from the UI per design spec
  const ignoreCameraOrientation = true;

  const poiWaypoint = set(
    initActionType("gotoWaypoint", GotoWaypointActionArgs.create()),
    "args.gotoWaypoint",
    {
      waypoint: {
        xy: {
          frame: xyFrame,
        },
        z: {
          // all z heights are nav even when creating outdoor GPS missions (gps heights are not accurate enough). Only exception is Site missions. Then everything is Site Frame
          frame: zFrame ?? (supportedFrames.has(xyFrame) ? xyFrame : FrameType_Enum.NAV_TAKEOFF),
        },
        heading: {
          // rotations are counter clockwise from due East. GPS should init to pointing north (0 degrees on the UI)
          value: xyFrame === FrameType_Enum.GPS ? PI_OVER_2 : 0,
          frame: xyFrame,
        },
        gimbalPitch: {
          value: 0,
        },
      },
      motionArgs: getFlyToPoiMotionArgs(
        ignoreCameraOrientation,
        flyByWaypoint,
        globalPathfinderEnabled,
        planAroundGeofences,
        rampArgs,
        undefined,
        undefined,
        speed
      ),
    }
  );
  return poiWaypoint;
}
const DEFAULT_ORBIT_ARGS = {
  radius: 10,
  speed: 5,
  numRotations: 1000,
  gimbalPitch: degToRad(45),
  hideAr: true,
};
export function createNewOrbitPoi({
  xyFrame,
  orbitArgs: orbitArgsInput,
  interceptMode: interceptModeInput,
  customIntercept: customInterceptInput,
  motionArgs: motionArgsInput,
  waypointFreeLookMode: waypointFreeLookModeInput,
  globalPathfinderEnabled,
  planAroundGeofences,
  zFrame,
  rampArgs,
}: Partial<GotoAndOrbitPoiActionArgs> & {
  xyFrame: FrameType_Enum;
  globalPathfinderEnabled?: boolean;
  planAroundGeofences?: boolean;
  zFrame?: FrameType_Enum;
  rampArgs?: RampArgs;
}): ActionType<"gotoAndOrbitPoi"> {
  const supportedFrames = new Set([FrameType_Enum.SITE, FrameType_Enum.NAV_TAKEOFF]);
  const orbitCenter = {
    xy: {
      frame: xyFrame,
    },
    z: {
      // all z heights are nav even when creating outdoor GPS missions (gps heights are not accurate enough). Only exception is Site missions. Then everything is Site Frame
      frame: zFrame ?? (supportedFrames.has(xyFrame) ? xyFrame : FrameType_Enum.NAV_TAKEOFF),
    },
  };
  const orbitArgs = OrbitPoiActionArgs.fromPartial({
    ...DEFAULT_ORBIT_ARGS,
    orbitCenter,
    ...orbitArgsInput,
  });
  const interceptMode = interceptModeInput ?? InterceptOrbitPoiMode_Enum.PERPENDICULAR;
  const customIntercept = customInterceptInput ?? WaypointPb.fromPartial({ ...orbitCenter });
  const motionArgs =
    motionArgsInput ??
    getFlyToPoiMotionArgs(true, true, globalPathfinderEnabled, planAroundGeofences, rampArgs);
  const waypointFreeLookMode = waypointFreeLookModeInput ?? FreeLookMode_Enum.PAN_AND_TILT;

  const args = GotoAndOrbitPoiActionArgs.fromPartial({
    orbitArgs,
    interceptMode,
    customIntercept,
    motionArgs,
    waypointFreeLookMode,
  });
  const orbitPoiAction = initActionType("gotoAndOrbitPoi", args);
  return orbitPoiAction;
}

export function createGPSInitAction({ height }: { height?: number } = {}): GPSInitWaypoint {
  const waypoint = createDefaultWaypoint({
    timedWaitInit: initActionType(
      "timedWait",
      TimedWaitActionArgs.fromPartial({ timeout: { value: 15 } })
    ),
    xyFrame: FrameType_Enum.NAV_TAKEOFF,
  });
  /**
   * These are the default values in the GPS init hack. And we use it because BNSF likes those values.
   * I'm not kidding, that's literally it. Distances are in meters and the timed wait is in seconds
   * The name is hard coded. It's how we tell that this is a GPS init hack and not an actual waypoint
   */
  waypoint.args.sequence.name = GPS_INIT_NAME;
  set(waypoint.args.sequence.actions[2].args.gotoWaypoint, "waypoint.xy.x", 2);
  set(waypoint.args.sequence.actions[2].args.gotoWaypoint, "waypoint.z.value", height ?? 3);
  return waypoint as GPSInitWaypoint;
}
